import { componentRegistry } from './runtime-component-registry.js';
import { componentLoader } from './runtime-component-loader.js';
import { createSingletonLogger } from './singleton-logger.js';

class RuntimeComponents {
  static componentName = "runtime-components";
  
  #logger;

  constructor() {
    this.#logger = createSingletonLogger(this);
  }

  /**
   * Register a component at runtime
   * @param {Object} options - Registration options
   * @param {string|Function|Object} options.source - Component source (path, class, URL, or code)
   * @param {Object} options.context - Optional map of external functions/classes to use in component
   * @param {string} options.type - Source type: 'module', 'function', 'url', or 'code'
   * @param {Object} options.descriptor - Component descriptor defining the public interface
   * @param {Array<string>} options.dependencies - Optional array of component namespaces this depends on
   * @returns {Promise<Object>} The registered component instance
   */
  async register(options) {
    const { source, context = {}, type = 'module', descriptor, dependencies = [] } = options;
    
    if (!source || !descriptor) {
      throw new Error("Missing required parameters: source and descriptor");
    }
    
    if (!descriptor.namespace) {
      throw new Error("Descriptor must have a namespace property");
    }
    
    this.#logger.log(`Initiating registration: ${descriptor.namespace}`);
    
    try {
      // Load the component implementation
      const implementation = await componentLoader.loadComponent(source, context, type);
      
      // Validate it's a proper component
      componentLoader.validateComponent(implementation);
      
      // Register the component
      const instance = await componentRegistry.registerComponent({
        namespace: descriptor.namespace,
        implementation,
        descriptor,
        dependencies
      });
      
      if (!instance) {
        this.#logger.error(`Failed to register runtime component ${descriptor.namespace}`);
        return null;
      }
      
      this.#logger.log(`Registration process completed: ${descriptor.namespace}`);
      
      return instance;
    } catch (error) {
      this.#logger.error(`Failed to register runtime component ${descriptor.namespace}: ${error.message}`);
      return null;
    }
  }
  
  /**
   * Unregister a runtime component
   * @param {string} namespace - Component namespace to unregister
   * @returns {Promise<void>}
   */
  async unregister(namespace) {
    if (!namespace) {
      throw new Error("Namespace is required");
    }
    
    this.#logger.log(`Unregistering runtime component: ${namespace}`);
    
    const success = await componentRegistry.unregisterComponent(namespace);
    
    if (!success) {
      this.#logger.error(`Failed to unregister runtime component ${namespace}`);
      return false;
    }
    
    this.#logger.log(`Successfully unregistered runtime component: ${namespace}`);
    return true;
  }
  
  /**
   * Check if a runtime component is registered
   * @param {string} namespace - Component namespace
   * @returns {boolean}
   */
  isRegistered(namespace) {
    return componentRegistry.has(namespace);
  }
  
  /**
   * Get the instance of a runtime component
   * @param {string} namespace - Component namespace
   * @returns {Object|undefined} Component instance or undefined if not found
   */
  getInstance(namespace) {
    return componentRegistry.getInstance(namespace);
  }
  
  /**
   * Get the descriptor of a runtime component
   * @param {string} namespace - Component namespace
   * @returns {Object|undefined} Component descriptor or undefined if not found
   */
  getDescriptor(namespace) {
    return componentRegistry.getDescriptor(namespace);
  }
  
  /**
   * Get all registered runtime component namespaces
   * @returns {Array<string>} Array of namespaces
   */
  getAllNamespaces() {
    return componentRegistry.getAllNamespaces();
  }
  
  /**
   * Batch register multiple components
   * @param {Array<Object>} components - Array of component registration options
   * @returns {Promise<Array<Object>>} Array of registered component instances
   */
  async registerMultiple(components) {
    const results = [];
    
    for (const component of components) {
      const instance = await this.register(component);
      
      if (instance) {
        results.push({ 
          success: true, 
          namespace: component.descriptor.namespace, 
          instance 
        });
      } else {
        results.push({ 
          success: false, 
          namespace: component.descriptor?.namespace || 'unknown'
        });
      }
    }
    
    return results;
  }
  
  /**
   * Batch unregister multiple components
   * @param {Array<string>} namespaces - Array of component namespaces
   * @returns {Promise<Array<Object>>} Array of unregistration results
   */
  async unregisterMultiple(namespaces) {
    const results = [];
    
    for (const namespace of namespaces) {
      const success = await this.unregister(namespace);
      
      results.push({ 
        success, 
        namespace 
      });
    }
    
    return results;
  }
}

const runtimeComponents = new RuntimeComponents();
export { RuntimeComponents, runtimeComponents };